import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Spinner,
  Badge,
  Table,
} from "reactstrap";
import moment from "moment";
import { getFirebaseAuthToken } from "../../helpers/firebase_helper";
import { formatCentsToDollars } from "../../utils/utils";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const authHeaders = async () => ({
  "Content-Type": "application/json",
  Authorization: `${await getFirebaseAuthToken()}`,
});

const money = (cents) => `$${formatCentsToDollars(cents || 0)}`;

const effDate = (ms) => (ms ? moment.utc(ms).format("MMM D, YYYY") : "—");

const roleLabel = (r) =>
  ({ broker: "Broker", ga: "GA", national: "National", admin: "Admin" }[r] ||
  r);

/**
 * Benefit-set commission rollup for one real employer. In Avero, an employer
 * has several benefit sets; in BaxRx each is its own managed group. This view
 * reunites them (by parentExternalId) and shows the employer total plus what
 * each benefit set contributed and where the numbers come from.
 */
const BenefitSetsRollup = () => {
  const { parentExternalId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/dashboard/benefit-sets/${encodeURIComponent(
            parentExternalId
          )}/rollup`,
          { headers: await authHeaders() }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            res.status === 404
              ? "No benefit sets found for this employer."
              : body.error || `Request failed (${res.status})`
          );
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parentExternalId]);

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center mb-4">
          <div>
            <h4 className="mb-0">
              {data?.parentName || parentExternalId}
            </h4>
            <p className="text-muted mb-0">
              Benefit sets · commission rollup
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardBody className="text-center text-muted py-5">
              {error}
            </CardBody>
          </Card>
        )}

        {!loading && !error && data && (
          <>
            {/* Employer totals */}
            <Row className="g-3 mb-2">
              <Col md={4}>
                <Card className="h-100">
                  <CardBody>
                    <p className="text-muted text-uppercase fw-medium mb-2 fs-12">
                      Total commission
                    </p>
                    <h3 className="mb-0">
                      {money(data.employerCommissionTotal)}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100">
                  <CardBody>
                    <p className="text-muted text-uppercase fw-medium mb-2 fs-12">
                      Total billed
                    </p>
                    <h3 className="mb-0">{money(data.employerBilledTotal)}</h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100">
                  <CardBody>
                    <p className="text-muted text-uppercase fw-medium mb-2 fs-12">
                      Benefit sets
                    </p>
                    <h3 className="mb-0">{data.setCount}</h3>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Per-set breakdown */}
            {(data.sets || []).map((set) => (
              <Card key={set.groupId} className="mt-3">
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h5 className="mb-0 me-2">
                    {set.benefitSetName || set.name || set.externalId}
                  </h5>
                  <Badge color="light" className="text-body border">
                    Effective {effDate(set.coverageEffectiveDate)}
                  </Badge>
                  <span className="ms-auto text-muted fs-13">
                    {set.invoiceCount} invoice
                    {set.invoiceCount === 1 ? "" : "s"}
                  </span>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={4}>
                      <p className="text-muted mb-1 fs-12 text-uppercase">
                        Commission
                      </p>
                      <h4 className="mb-0">{money(set.commissionTotal)}</h4>
                    </Col>
                    <Col md={4}>
                      <p className="text-muted mb-1 fs-12 text-uppercase">
                        Billed
                      </p>
                      <h4 className="mb-0 text-muted">
                        {money(set.billedTotal)}
                      </h4>
                    </Col>
                    <Col md={4}>
                      <p className="text-muted mb-1 fs-12 text-uppercase">
                        By role
                      </p>
                      {set.byRole && set.byRole.length ? (
                        set.byRole.map((r) => (
                          <div
                            key={r.role}
                            className="d-flex justify-content-between"
                            style={{ maxWidth: 220 }}
                          >
                            <span>{roleLabel(r.role)}</span>
                            <span className="fw-medium">{money(r.amount)}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Col>
                  </Row>

                  {set.byPeriod && set.byPeriod.length > 0 && (
                    <div className="mt-3 table-responsive">
                      <Table size="sm" className="mb-0 align-middle">
                        <thead>
                          <tr className="text-muted fs-12 text-uppercase">
                            <th>Period</th>
                            <th className="text-end">Commission</th>
                            <th className="text-end">Billed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {set.byPeriod.map((p) => (
                            <tr key={p.period}>
                              <td>
                                {moment.utc(p.period).isValid()
                                  ? moment.utc(p.period).format("MMM YYYY")
                                  : p.period}
                              </td>
                              <td className="text-end">
                                {money(p.commission)}
                              </td>
                              <td className="text-end text-muted">
                                {money(p.billed)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}

            {(!data.sets || data.sets.length === 0) && (
              <Card>
                <CardBody className="text-center text-muted py-5">
                  This employer has no benefit sets yet.
                </CardBody>
              </Card>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default BenefitSetsRollup;
